import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Helmet } from 'react-helmet';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    if (error) {
      setError(null);
    }
  };

  return (
    <div className={`forgot-page ${visible ? 'forgot-page--visible' : ''}`}>
      <Helmet>
        <title>DHH Collab | Forgot Password</title>
        <meta name="description" content="Reset your DHH Collab account password." />
      </Helmet>

      <div className="forgot-page__bg" aria-hidden>
        <span className="forgot-page__blob forgot-page__blob--1" />
        <span className="forgot-page__blob forgot-page__blob--2" />
        <span className="forgot-page__grid" />
      </div>

      <div className="forgot-page__inner container-padding">
        <div className="forgot-page__layout">
          <aside className="forgot-page__visual" aria-hidden>
            <span className="forgot-page__eyebrow">Account Recovery</span>
            <h1 className="forgot-page__title">Reset Access</h1>
            <p className="forgot-page__lead">
              We&apos;ll send a secure link to your inbox so you can choose a new password and get back to the
              booth.
            </p>

            <div className="forgot-page__graphic">
              <div className="forgot-page__signal">
                <span className="forgot-page__signal-ring" />
                <span className="forgot-page__signal-ring forgot-page__signal-ring--2" />
                <span className="forgot-page__signal-ring forgot-page__signal-ring--3" />
              </div>
              <div className="forgot-page__envelope">
                <div className="forgot-page__envelope-body" />
                <div className="forgot-page__envelope-flap" />
                <div className="forgot-page__envelope-letter" />
              </div>
              <span className="forgot-page__spark forgot-page__spark--1" />
              <span className="forgot-page__spark forgot-page__spark--2" />
              <span className="forgot-page__spark forgot-page__spark--3" />
            </div>
          </aside>

          <div className="forgot-page__card">
            <header className="forgot-page__card-head">
              <h2 className="forgot-page__card-title">Reset your password</h2>
              <p className="forgot-page__card-subtitle">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
            </header>

            {success ? (
              <div className="forgot-page__success">
                <div className="forgot-page__success-box">
                  <p className="forgot-page__success-title">Check your email!</p>
                  <p className="forgot-page__success-text">
                    We&apos;ve sent password reset instructions to{' '}
                    <strong className="forgot-page__success-email">{email}</strong>
                  </p>
                  <p className="forgot-page__success-text">
                    If you don&apos;t see the email, check your spam folder.
                  </p>
                </div>
                <Link to="/login" className="forgot-page__back">
                  Back to login
                </Link>
              </div>
            ) : (
              <form className="forgot-page__form" onSubmit={handleSubmit}>
                <div className="forgot-page__field">
                  <label htmlFor="email" className="forgot-page__label">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      clearError();
                    }}
                    className="forgot-page__input"
                    placeholder="Enter your email address"
                  />
                </div>

                {error && <div className="forgot-page__error">{error}</div>}

                <button
                  type="submit"
                  disabled={loading}
                  className={`forgot-page__submit ${loading ? 'forgot-page__submit--disabled' : 'forgot-page__submit--ready'}`}
                >
                  {loading ? 'Sending...' : 'Send reset instructions'}
                </button>

                <Link to="/login" className="forgot-page__back">
                  Back to login
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
